import { useRef } from "react";
import { Check, UserRound, ChevronLeft, ChevronRight } from "lucide-react";

export function TemplatePreview({ template, testId }) {
  const style = template?.style || {};
  const look = style.look || "bold";
  const words = (template?.preview_text || "Your next great moment").split(" ");
  return (
    <div data-testid={testId} className={`style-preview style-preview--${look}`} style={{ "--style-accent": style.highlight_color, "--style-text": style.primary_color }}>
      <span className="style-preview-headline">One idea.<br />Worth sharing.</span>
      <div className="style-preview-footage"><UserRound size={92} strokeWidth={1} /><span>Speaker focus</span></div>
      <span className={`style-preview-caption ${style.uppercase ? "uppercase" : ""}`}>
        {words.map((word, i) => <span key={i} className={i === 1 ? "style-preview-active" : ""}>{word} </span>)}
      </span>
      <span className="style-preview-label">Style illustration</span>
    </div>
  );
}

export function TemplatePicker({ templates, value, onChange, prefix = "style", disabled = false, loading = false }) {
  const scroller = useRef(null);
  const selected = templates.find(t => t.id === value);
  return (
    <div data-testid={`${prefix}-picker`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] uppercase tracking-widest text-copy-500">Choose a look</span>
        <div className="flex items-center gap-2">
          <span data-testid={`${prefix}-scroll-hint`} className="text-[10px] text-copy-400">Browse all styles</span>
          <button type="button" data-testid={`${prefix}-previous`} aria-label="Previous styles" disabled={disabled || loading} onClick={() => scroller.current?.scrollBy({ left: -300, behavior: 'smooth' })} className="p-1 rounded border border-surface-700 hover:bg-surface-800"><ChevronLeft size={16} /></button>
          <button type="button" data-testid={`${prefix}-next`} aria-label="Next styles" disabled={disabled || loading} onClick={() => scroller.current?.scrollBy({ left: 300, behavior: 'smooth' })} className="p-1 rounded border border-surface-700 hover:bg-surface-800"><ChevronRight size={16} /></button>
        </div>
      </div>
      {loading && <div role="status" data-testid={`${prefix}-loading`} className="h-32 flex items-center justify-center rounded-xl border border-surface-800 bg-surface-900 text-sm text-copy-400">Loading styles…</div>}
      {!loading && templates.length === 0 && <p data-testid={`${prefix}-empty`} className="text-sm text-copy-400 py-4">Styles are unavailable. Please reopen this panel to try again.</p>}
      <div ref={scroller} className="flex gap-3 overflow-x-auto pb-3 snap-x">
        {templates.map(t => (
          <button type="button" key={t.id} disabled={disabled} data-testid={`${prefix}-option-${t.id}`}
            aria-pressed={value === t.id} onClick={() => onChange(t.id)}
            className={`w-36 shrink-0 snap-start text-left rounded-xl overflow-hidden border transition-[border-color,transform] hover:-translate-y-0.5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-cyan-400 ${value === t.id ? "border-cyan-400" : "border-surface-800 hover:border-surface-500"}`}>
            <TemplatePreview template={t} testId={`${prefix}-preview-${t.id}`} />
            <span className="flex items-center justify-between gap-1 px-2.5 py-2.5 text-xs font-semibold bg-surface-950">
              {t.name}{value === t.id && <Check className="w-3.5 h-3.5 text-cyan-400 shrink-0" />}
            </span>
          </button>
        ))}
      </div>
      {selected && <p data-testid={`${prefix}-description`} className="text-sm text-copy-400 mt-1"><span className="text-copy-200">{selected.best_for || "Your custom style"}.</span> {selected.description}</p>}
    </div>
  );
}
